import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmDialogComponent } from '../../components/dialogs/confirm-dialog/confirm-dialog.component';
import { McpConnectorDialogComponent } from '../../components/dialogs/mcp-connector-dialog/mcp-connector-dialog.component';
import {
  AlTableBadgeComponent,
  AlTableBadgeVariant,
} from '../../components/features/table-badge/table-badge.component';
import { AlTableTextComponent } from '../../components/features/table-text/table-text.component';
import { AlTableColumn, AlTableRowAction } from '../../components/features/table/table.component';
import { MCP_AUTO_DISABLE_AFTER_FAILURES, McpConnector, McpService } from '../../services/mcp.service';

/** Table row: the connector plus the derived cells, built once per load
 *  (CLAUDE.md rule 18 — never a getter that builds arrays for app-table). */
interface McpRow extends McpConnector {
  toolsLabel: string;
  agentsCount: number;
}

/**
 * KI-Agenten › MCP-Connectoren — the client's connectors (admin/manager).
 * A connector is a third party's service: it is created inactive, its tools
 * are reviewed one by one on the detail page, and it only runs after the
 * responsibility step.
 */
@Component({
  selector: 'app-mcp-connectors',
  templateUrl: './mcp-connectors.component.html',
  styleUrl: './mcp-connectors.component.scss',
})
export class McpConnectorsComponent implements OnInit {
  rows: McpRow[] = [];
  isLoading = false;
  columns: AlTableColumn[] = [];
  rowActions: AlTableRowAction[] = [];
  globalActions: { key: string; title: string; icon: string }[] = [];

  constructor(
    private mcp: McpService,
    private dialogService: NbDialogService,
    private toastr: NbToastrService,
    private router: Router,
    private t: TranslateService
  ) {}

  ngOnInit(): void {
    this.buildTable();
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.mcp.list().subscribe({
      next: (data) => {
        this.rows = data.map((c) => ({
          ...c,
          toolsLabel: `${c.tools.filter((tool) => tool.is_enabled && !tool.removed).length} / ${
            c.tools.filter((tool) => !tool.removed).length
          }`,
          agentsCount: c.assigned_bot_ids.length,
        }));
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.toastr.danger(this.t.instant('pages.mcp.errors.loadFailed'), '');
      },
    });
  }

  statusBadge(c: McpConnector): { label: string; variant: AlTableBadgeVariant; tooltip?: string } {
    if (c.is_active) {
      return { label: this.t.instant('pages.mcp.status.active'), variant: 'success', tooltip: this.t.instant('pages.mcp.tooltips.statusActive') };
    }
    if (c.consecutive_failures >= MCP_AUTO_DISABLE_AFTER_FAILURES) {
      return { label: this.t.instant('pages.mcp.status.autoDisabled'), variant: 'danger', tooltip: c.disabled_reason };
    }
    if (!c.acknowledgement_current) {
      return { label: this.t.instant('pages.mcp.status.needsAck'), variant: 'warning', tooltip: this.t.instant('pages.mcp.tooltips.statusNeedsAck') };
    }
    return { label: this.t.instant('pages.mcp.status.inactive'), variant: 'muted', tooltip: this.t.instant('pages.mcp.tooltips.statusInactive') };
  }

  private buildTable(): void {
    this.columns = [
      {
        key: 'name',
        kind: 'name',
        title: this.t.instant('pages.mcp.fields.name'),
        width: '24%',
        sortable: true,
        component: AlTableTextComponent,
        componentInputs: ({ row }) => ({ text: row.name, max: 80, suffix: row.slug }),
      },
      {
        key: 'server_url',
        kind: 'text',
        title: this.t.instant('pages.mcp.fields.serverUrl'),
        width: '28%',
        component: AlTableTextComponent,
        componentInputs: ({ value }) => ({ text: value, max: 80 }),
      },
      {
        key: 'is_active',
        title: this.t.instant('words.status'),
        width: '16%',
        component: AlTableBadgeComponent,
        componentInputs: ({ row }) => this.statusBadge(row),
      },
      { key: 'toolsLabel', title: this.t.instant('pages.mcp.fields.toolsEnabled'), width: '10%' },
      { key: 'agentsCount', kind: 'number', title: this.t.instant('pages.mcp.fields.agents'), width: '8%', sortable: true },
      { key: 'calls_30d', kind: 'number', title: this.t.instant('pages.mcp.fields.calls30d'), width: '8%', sortable: true },
      { key: 'failed_calls_30d', kind: 'number', title: this.t.instant('pages.mcp.fields.failed30d'), width: '6%', sortable: true },
    ];
    this.rowActions = [
      { key: 'open', title: this.t.instant('pages.mcp.open'), icon: 'eye-outline' },
      { key: 'delete', title: this.t.instant('buttons.delete'), icon: 'trash-outline' },
    ] as AlTableRowAction[];
    this.globalActions = [{ key: 'create', title: this.t.instant('pages.mcp.add'), icon: 'plus-outline' }];
  }

  onTableEvent(payload: { event: string; data: any }): void {
    if (payload.event === 'globalAction') {
      this.create();
      return;
    }
    if (payload.event !== 'rowAction') return;
    const row = payload.data?.row as McpRow;
    if (!row) return;
    if (payload.data?.action?.key === 'open') this.open(row);
    if (payload.data?.action?.key === 'delete') this.confirmDelete(row);
  }

  open(row: McpConnector): void {
    this.router.navigate(['/mcp-connectors', row.id]);
  }

  create(): void {
    this.dialogService.open(McpConnectorDialogComponent, {
      context: {
        connector: null,
        onSaved: (saved: McpConnector) => {
          this.toastr.success(this.t.instant('pages.mcp.created', { count: saved.tools.length }), '');
          this.router.navigate(['/mcp-connectors', saved.id]);
        },
      },
    });
  }

  private confirmDelete(row: McpConnector): void {
    const ref = this.dialogService.open(ConfirmDialogComponent, {
      context: {
        title: 'pages.mcp.deleteTitle',
        message: this.t.instant('pages.mcp.deleteMessage', { name: row.name, agents: row.assigned_bot_ids.length }),
        onConfirm: () => {
          ref.close();
          this.mcp.delete(row.id).subscribe({
            next: () => {
              this.toastr.success(this.t.instant('pages.mcp.deleted'), '');
              this.load();
            },
            error: () => this.toastr.danger(this.t.instant('pages.mcp.errors.saveFailed'), ''),
          });
        },
      },
    });
  }
}
